
import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();

  // Handle scroll event to change navbar appearance
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  const isActive = (path: string) => {
    return location.pathname === path;
  };
  
  const linkClass = (path: string) => {
    return cn("font-display font-medium transition-colors whitespace-nowrap", 
      isActive(path) ? "text-white" : "text-gold hover:text-white");
  };

  // Function to scroll to top when clicking navigation items
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'instant'
    });
  };

  // Navigation items with correct paths - Portfolio removed
  const navItems = [
    {
      path: "/",
      label: "Home"
    }, 
    {
      path: "/about",
      label: "Who We Are"
    }, 
    {
      path: "/services",
      label: "What We Do"
    }, 
    {
      path: "/services/process",
      label: "How We Do It"
    }
  ];
  
  return (
    <nav className={cn('fixed top-0 left-0 right-0 z-50 transition-all duration-300 py-3 sm:py-4 px-3 sm:px-4 md:px-6 lg:px-10', 
      'bg-black shadow-md')}
    >
      <div className="container mx-auto">
        <div className="flex items-center justify-between">
          {/* Logo - with fixed aspect ratio */}
          <Link to="/" onClick={scrollToTop} className="flex items-center">
            <img 
              src="/lovable-uploads/071231bc-7ebb-4c11-b2d8-241e00e8cb83.png" 
              alt="LovaBuild Logo" 
              className="h-16 sm:h-18 md:h-20 w-auto object-contain" 
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4 lg:space-x-8">
            {navItems.map(item => (
              <Link 
                key={item.path} 
                to={item.path} 
                onClick={scrollToTop}
                className={linkClass(item.path)}
              >
                {item.label}
              </Link>
            ))}
            <Link to="/booking" onClick={scrollToTop}>
              <Button 
                starBorder 
                size={isMobile ? "sm" : "default"} 
                className="ml-2 md:ml-4 font-cta"
              >
                Let's Talk
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <div className="relative">
              <div className="absolute inset-0 bg-black rounded-full shadow-xl border border-gold/50"></div>
              
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} 
                className="relative text-gold hover:text-white p-2 rounded-full z-10" 
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              >
                {isMobileMenuOpen ? <X className="h-5 w-5 sm:h-6 sm:w-6" /> : <Menu className="h-5 w-5 sm:h-6 sm:w-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={cn(
        'fixed inset-0 top-16 bg-black z-40 transform transition-transform duration-300 px-4 sm:px-6 py-6 sm:py-8', 
        'shadow-xl border-t border-gold/50', 
        isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
      )}>
        <div className="flex flex-col space-y-5 sm:space-y-6 bg-black mx-0 px-3 py-2 sm:px-[12px] sm:py-[7px]">
          {navItems.map(item => (
            <Link 
              key={item.path} 
              to={item.path} 
              className={cn("text-base sm:text-lg font-display font-medium", 
                isActive(item.path) ? "text-white" : "text-gold hover:text-white")} 
              onClick={() => {
                setIsMobileMenuOpen(false);
                scrollToTop();
              }}
            >
              {item.label}
            </Link>
          ))}
          <Link to="/booking" className="w-full" onClick={() => {
            setIsMobileMenuOpen(false);
            scrollToTop();
          }}>
            <Button 
              starBorder 
              className="mt-2 w-full font-cta"
            >
              Let's Talk
            </Button>
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
